import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { TariffCard } from '../../components/tariff/tariff-card';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { Breakpoint } from '../../utils/breakpoint';
import { TariffInfoPanel } from '../../components/tariff/tariff-info-panel';
import { TariffCardDetailsModal } from '../../components/tariff/tariff-card-details-modal';
import { useAppSelector } from '../../hooks/redux';
import { ITariff, TariffTypes } from '../../store/tariff.slice';
import { getIsActiveOrScheduledProps } from '../../utils/tariffUtils';
import { TariffBuyModal } from '../../components/tariff/tariff-buy-modal';
import { TariffPurchaseWelcome } from '../../components/tariff/tariff-purchase-welcome';
import { usePosthogCapture } from '../../hooks/usePosthogCapture';

export const TariffPlanTab = () => {
  const tariffState = useAppSelector((state) => state.tariffSlice);
  // const planHistoryState = useAppSelector((state) => state.planHistorySlice);

  const [showDetailsDialog, setShowDetails] = useState<Set<number>>(new Set());
  const [showByDialog, setShowBuyDialog] = useState<ITariff | null>(null);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState<boolean>(false);

  const breakpoint = useBreakpoint();
  const captureEvent = usePosthogCapture();
  const hasLoggedTariffView = useRef(false);

  useEffect(() => {
    setShowDetails(new Set()); //clear all if BP changed
  }, [breakpoint]);

  const onDetailsClick = useCallback((index: number) => {
    setShowDetails((v) => {
      const t = new Set(v);
      t.add(index);

      return t;
    });
  }, []);

  const onBuyClick = useCallback(
    (tariff: ITariff) => {
      if (tariff.price <= 0) return;

      if (tariffState.scheduled.length > 0) {
        toast.info(
          'У вас уже куплены запланированные к использованию тарифы. В текущий момент покупка нового тарифа не возможна.',
        );

        return;
      }
      captureEvent('tariff_purchase_initiated', {
        tariff_id: tariff.id,
        price: tariff.price,
        currency: tariff.currency_code,
      });
      setShowBuyDialog(tariff);
    },
    [captureEvent, tariffState.scheduled.length],
  );

  const onByClose = useCallback((paymentDone?: boolean) => {
    setShowBuyDialog(null);
    if (paymentDone) {
      setShowWelcomeDialog(true);
    }
  }, []);

  const onCloseDetailedModalClick = useCallback(() => {
    setShowDetails((v) => {
      return new Set();
    });
  }, []);

  const hasPromo = useMemo(() => {
    return tariffState.current.filter((t) => t.type === TariffTypes.PROMO).length > 0;
  }, [tariffState]);

  const tariffsToShow = useMemo(
    () => (hasPromo ? tariffState.tariffs : tariffState.tariffs.filter((t) => t.type !== TariffTypes.PROMO)),
    [hasPromo, tariffState.tariffs],
  );

  useEffect(() => {
    if (hasLoggedTariffView.current) {
      return;
    }

    captureEvent('tariff_plan_viewed', {
      has_active_tariff: tariffState.current.length > 0,
    });
    hasLoggedTariffView.current = true;
  }, [captureEvent, tariffState.current.length, tariffsToShow.length]);

  return (
    <>
      {showDetailsDialog.size > 0 && breakpoint > Breakpoint.Md && (
        <TariffCardDetailsModal
          tariffs={tariffsToShow}
          index={showDetailsDialog.values().next().value}
          onByClick={(t) => {
            onCloseDetailedModalClick();
            onBuyClick(t);
          }}
          onClose={onCloseDetailedModalClick}
          tariffToolsMaxCount={tariffState.tariffsToolsMaxCount}
        />
      )}
      {showByDialog && <TariffBuyModal tariff={showByDialog} onClose={onByClose} />}

      {showWelcomeDialog && <TariffPurchaseWelcome onClose={() => setShowWelcomeDialog(false)} />}

      <div className="flex flex-col gap-3">
        {(breakpoint <= Breakpoint.Lg || !hasPromo) && tariffsToShow.length > 0 && (
          <div className="m-3">
            <TariffInfoPanel isPromo={hasPromo} />
          </div>
        )}
        {breakpoint > Breakpoint.Lg && tariffsToShow.length > 0 && hasPromo && (
          <div className="flex flex-row flex-nowrap gap-3">
            <TariffCard
              tariffToolsMaxCount={tariffState.tariffsToolsMaxCount}
              tariff={tariffsToShow[0]}
              displayMode={breakpoint <= Breakpoint.Md && showDetailsDialog.has(0) ? 'full' : 'shot'}
              onDetailsClick={() => onDetailsClick(0)}
              onSelectClick={() => onBuyClick(tariffsToShow[0])}
              {...getIsActiveOrScheduledProps(tariffsToShow[0].id, tariffState.current, tariffState.scheduled)}
            />
            <TariffInfoPanel isPromo={hasPromo} />
          </div>
        )}
        <div className="flex flex-row flex-wrap gap-3 justify-center lg:justify-start">
          {tariffsToShow.map((t, index) => {
            if (index === 0 && breakpoint > Breakpoint.Lg && hasPromo) {
              //skip first
              return null;
            }
            const activeProps = getIsActiveOrScheduledProps(t.id, tariffState.current, tariffState.scheduled);

            return (
              <TariffCard
                tariffToolsMaxCount={tariffState.tariffsToolsMaxCount}
                tariff={t}
                displayMode={breakpoint <= Breakpoint.Md && showDetailsDialog.has(index) ? 'full' : 'shot'}
                key={index}
                onDetailsClick={() => onDetailsClick(index)}
                onSelectClick={() => onBuyClick(t)}
                {...activeProps}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
